import { FormEvent, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard, Icon } from '../components/Icon';
import { smooth, fmtDate, fmtDateFull, tickIdxs } from '../components/Charts';
import { useModalEscape } from '../lib/useModalEscape';

interface Account {
  id: string; email: string; name: string | null; company: string | null;
  plan_id: string | null; plan_name: string | null; status: string;
  credits_balance: number; credits_used_total: number;
  sites_count: number; max_sites: number; widgets_count: number;
  account_type: string | null;
  /** Full key: only sent to staff who can edit accounts (accounts:write / superadmin). */
  license_key?: string | null;
  /** Last 8 characters of the key: sent to every role. */
  license_last8?: string | null;
  created_at: string; last_login_at: string | null;
  member_count?: number; is_member?: boolean;
}
interface Conversation {
  id: string; title?: string; created_at?: string; last_message_at?: string;
  message_count?: number; first_message?: string; site_url?: string;
}
interface Message {
  id: string; role: string; model?: string | null; content?: string | null;
  input_tokens?: number; output_tokens?: number; cost_usd?: number; created_at?: string;
}
interface Site { id: string; site_url?: string; account_email?: string; plugin_version?: string; wp_version?: string; last_seen_at?: string; is_active?: boolean }
interface Plan { id: string; name?: string }
interface CreditTx {
  id: string; transaction_type: string; credits_amount: number;
  balance_after: number; description?: string | null; created_at?: string;
}
interface AccountDetail {
  account: Record<string, any>;
  sites?: { site_url?: string; domain?: string; plugin_version?: string; last_seen_at?: string }[];
  creditHistory?: CreditTx[];
  onboarding?: { onboarding_completed?: boolean; onboarding_data?: Record<string, any> } | null;
}
interface UsageTxn {
  date: string; type: string; amount: number; balanceAfter: number; description?: string | null;
}
interface UsageResp {
  usage?: { date: string; credits: number; messages: number; cost?: number }[];
  transactions?: UsageTxn[];
  summary?: { totalAdmin?: number; totalPurchased?: number; totalUsed?: number; totalMessages?: number } | null;
}

/** Which panel the expanded row is showing — one row, several modes. */
type Mode = 'view' | 'sites' | 'conversations' | 'email' | 'edit' | 'usage';

const CREATE_PLANS = ['standard', 'agency', 'internal', 'pilot', 'free', 'pro_500', 'pro_3000', 'pro_10000'];
const ACCOUNT_TYPES = ['customer', 'staff', 'pilot', 'grandfathered'];
const FILTER_PLANS = ['standard', 'agency', 'internal', 'pilot', 'beta', 'test', 'free', 'pro_500', 'pro_3000', 'pro_10000'];

/** A throwaway demo account the cleanup tool may delete: trial-* email still tagged customer. */
const isTrialJunk = (a: Account) => /^trial-/i.test(a.email || '') && (a.account_type || 'customer') === 'customer';

// Agencies — accounts with a team OR on the agency plan (even with no members
// yet) — and their member accounts live in the Agency page, not here.
export const isAgency = (a: { member_count?: number; plan_id?: string | null }) =>
  (Number(a.member_count) || 0) > 0 || (a.plan_id || '') === 'agency';
// Partner-managed accounts (partner_external_ref / account_type) belong to the
// Partners module, not the general Accounts list.
const isPartnerManaged = (a: Account) =>
  (a.account_type || '') === 'partner_managed' || !!(a as { partner_external_ref?: string | null }).partner_external_ref;
// Accounts that live in another module and must NOT appear in the general list.
const isAgencyOrMember = (a: Account) => isAgency(a) || !!a.is_member || isPartnerManaged(a);

export default function Accounts() {
  const { can } = useAuth();
  const canWrite = can('accounts', 'write');

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState(''); const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeF, setTypeF] = useState(''); const [planF, setPlanF] = useState(''); const [statusF, setStatusF] = useState('');
  const [sort, setSort] = useState('newest');
  const [open, setOpen] = useState<{ id: string; mode: Mode } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [a, s, dash] = await Promise.all([
        backendApi.get<{ accounts: Account[] }>('/license/admin/accounts'),
        backendApi.get<{ sites: Site[] }>('/license/admin/sites').catch(() => ({ sites: [] })),
        backendApi.get<any>('/license/admin/dashboard').catch(() => ({})),
      ]);
      setAccounts(a.accounts); setSites(s.sites || []);
      setPlans(Array.isArray(dash.plans) ? dash.plans : []);
      setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to accounts.' : err.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const rows = accounts.filter((a) => {
      if (isAgencyOrMember(a)) return false; // agencies + their members live in the Agency page
      const q = search.toLowerCase();
      if (q && ![a.email, a.name, a.company, a.license_key, a.license_last8].some((v) => v?.toLowerCase().includes(q))) return false;
      if (typeF === 'trial-junk') { if (!isTrialJunk(a)) return false; }
      else if (typeF && (a.account_type || 'customer') !== typeF) return false;
      if (planF && a.plan_id !== planF) return false;
      if (statusF && a.status !== statusF) return false;
      return true;
    });
    return [...rows].sort((a, b) => {
      switch (sort) {
        case 'oldest': return +new Date(a.created_at) - +new Date(b.created_at);
        case 'email_az': return a.email.localeCompare(b.email);
        case 'email_za': return b.email.localeCompare(a.email);
        case 'most_used': return b.credits_used_total - a.credits_used_total;
        case 'least_remaining': return (a.credits_balance || 0) - (b.credits_balance || 0);
        case 'most_sites': return b.sites_count - a.sites_count;
        default: return +new Date(b.created_at) - +new Date(a.created_at);
      }
    });
  }, [accounts, search, typeF, planF, statusF, sort]);

  // Only standalone accounts belong on this page (exclude agencies + members).
  const scoped = useMemo(() => accounts.filter((a) => !isAgencyOrMember(a)), [accounts]);
  const stats = useMemo(() => ({
    total: scoped.length,
    active: scoped.filter((a) => a.status === 'active').length,
    sites: scoped.reduce((s, a) => s + (Number(a.sites_count) || 0), 0),
    creditsUsed: scoped.reduce((s, a) => s + (Number(a.credits_used_total) || 0), 0),
  }), [scoped]);

  // Trial-junk cleanup counts (across all accounts, not just the filtered view).
  const junk = useMemo(() => accounts.filter(isTrialJunk), [accounts]);
  const junkCancelled = junk.filter((a) => a.status === 'cancelled').length;

  const broadcastFilters = useMemo(() => {
    const f: Record<string, string> = {};
    if (planF) f.plan = planF;
    if (statusF) f.status = statusF;
    if (search.trim()) f.search = search.trim();
    return f;
  }, [planF, statusF, search]);

  const toggle = (id: string, mode: Mode) =>
    setOpen(open && open.id === id && open.mode === mode ? null : { id, mode });


  async function remove(a: Account) {
    if (!confirm(`Delete account ${a.email}?\n\nThis permanently removes the account and cannot be undone.`)) return;
    setError(''); setNote('');
    try {
      await backendApi.delete(`/license/admin/accounts/${a.id}`);
      setNote(`${a.email} deleted.`); setOpen(null); await load();
    } catch (err: any) {
      /* THE SECOND QUESTION, asked only when there is a team behind the row.
       *
       * Deleting an account cascades: its workspace's memberships, invites,
       * site assignments and member keys all go with it, and its sites are
       * deleted outright. So one click here can strip a whole agency — and the
       * question above asks the same thing of a twenty-person agency as of an
       * abandoned trial, which is not where you want to learn the difference.
       *
       * The backend refuses such a delete once with 409 `owns_workspace` and
       * says how many people it would cut off. Without this branch that refusal
       * is a dead end: the red line reads "re-send with confirm=true", which
       * nobody can do from a panel, so an account with members could not be
       * deleted here at all. The guard is meant to be a speed bump, not a wall.
       *
       * SAFE TO SHIP FIRST. Nothing answers with this code until the backend
       * change lands, so until then the branch is simply never entered. */
      if (err.body?.code === 'owns_workspace') {
        const n = err.body.memberCount as number | undefined;
        const who = n === 1 ? '1 member' : `${n ?? 'several'} members`;
        if (!confirm(
          `${a.email} owns a workspace with ${who}.\n\n`
          + 'Deleting it removes their access, their assigned sites and their credit budgets, '
          + 'and deletes the sites on this account.\n\nThis cannot be undone. Delete anyway?'
        )) { setNote(''); return; }
        try {
          await backendApi.delete(`/license/admin/accounts/${a.id}?confirm=true`);
          setNote(`${a.email} and its workspace (${who}) deleted.`); setOpen(null); await load();
        } catch (err2: any) { setError(err2.body?.error || err2.message); }
        return;
      }
      setError(err.body?.error || err.message);
    }
  }

  async function deleteTrialJunk(scope: 'cancelled' | 'all') {
    const targets = scope === 'cancelled' ? junk.filter((a) => a.status === 'cancelled') : junk;
    if (!targets.length) { setError('Nothing to delete.'); return; }
    const label = scope === 'cancelled' ? 'cancelled (torn-down) trials' : 'ALL trial junk, including active demos';
    if (!confirm(`Permanently delete ${targets.length} ${label}, and all their data?\n\nThis cannot be undone. Real customers and team accounts are never affected.`)) return;
    setError(''); setNote('');
    try {
      const d = await backendApi.post<{ deleted?: number }>('/license/admin/accounts/delete-trial-junk', { scope });
      setNote(`Deleted ${d.deleted ?? 0} trial-junk account${d.deleted === 1 ? '' : 's'}.`);
      await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Individuals</h1>
          <p className="muted">{filtered.length === scoped.length
            ? `${scoped.length} account${scoped.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${scoped.length}`}</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-sm btn-ghost" onClick={() => { setShowBroadcast(true); setShowAdd(false); }}>
            <Icon name="mail" size={15} /> Broadcast</button>
          {canWrite && <button className="btn btn-sm btn-primary" onClick={() => { setShowAdd(true); setShowBroadcast(false); }}>
            + Add Account</button>}
        </div>
      </header>

      <div className="stat-row">
        <StatCard icon="users" tint="purple" value={stats.total} label="Accounts" sub={`${stats.active} active`} />
        <StatCard icon="globe" tint="green" value={stats.sites} label="Sites" />
        <StatCard icon="chart" tint="amber" value={stats.creditsUsed.toLocaleString()} label="Credits Used" />
      </div>

      {showAdd && canWrite && (
        <AddAccountForm onClose={() => setShowAdd(false)}
          onCreated={(m) => { setNote(m); setShowAdd(false); void load(); }} />
      )}
      {showBroadcast && (
        <div className="modal-scrim" onClick={() => setShowBroadcast(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2 className="card-title" style={{ margin: 0 }}>Broadcast email</h2>
              <button className="btn btn-sm btn-ghost" onClick={() => setShowBroadcast(false)}>Close</button>
            </div>
            <BroadcastForm count={filtered.length} filters={broadcastFilters}
              onClose={() => setShowBroadcast(false)} onSent={(m) => { setNote(m); setShowBroadcast(false); }} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="filter-bar">
          <input placeholder="Search email, name, company, license…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: '1 1 240px' }} />
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)}>
            <option value="">All Types</option>
            <option value="customer">Customers</option>
            <option value="staff">Staff</option><option value="pilot">Pilot</option>
            <option value="grandfathered">Grandfathered</option>
            <option value="trial-junk">Trial junk</option>
          </select>
          <select value={planF} onChange={(e) => setPlanF(e.target.value)}>
            <option value="">All Plans</option>
            {FILTER_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="">All Status</option><option value="active">Active</option>
            <option value="suspended">Suspended</option><option value="cancelled">Cancelled</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest First</option><option value="oldest">Oldest First</option>
            <option value="email_az">Email A–Z</option><option value="email_za">Email Z–A</option>
            <option value="most_used">Most Credits Used</option><option value="least_remaining">Least Credits Left</option>
            <option value="most_sites">Most Sites</option>
          </select>
        </div>
      </div>

      {typeF === 'trial-junk' && canWrite && (
        <div className="inline-form" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
          <h4 className="detail-h" style={{ marginTop: 0 }}>Trial junk cleanup</h4>
          <p className="muted small" style={{ margin: '0 0 12px' }}>
            {junk.length} throwaway demo account{junk.length === 1 ? '' : 's'} ({junkCancelled} cancelled, {junk.length - junkCancelled} active).
            Deletes only these, never a real customer or team account.
          </p>
          <div className="row-actions">
            <button className="btn btn-sm btn-danger" disabled={!junkCancelled} onClick={() => deleteTrialJunk('cancelled')}>
              Delete cancelled ({junkCancelled})</button>
            <button className="btn btn-sm btn-danger" disabled={!junk.length} onClick={() => deleteTrialJunk('all')}>
              Delete all ({junk.length})</button>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}

      <div className="card">
        {loading ? <p className="muted">Loading…</p> : filtered.length === 0 ? <p className="muted">No accounts match filters.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>User</th><th>Usage</th><th>Joined</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map((a) => {
                  const total = (Number(a.credits_used_total) || 0) + (Number(a.credits_balance) || 0);
                  const pct = total ? Math.round((Number(a.credits_used_total) / total) * 100) : 0;
                  // Same thresholds as the legacy dashboard: green until half used, amber past 50%, red past 80%.
                  const barColor = pct > 80 ? '#EF4444' : pct > 50 ? 'var(--color-status-warning-fg)' : 'var(--color-status-success-accent)';
                  const accountSites = sites.filter((s) => s.account_email === a.email);
                  return (
                      <tr key={a.id}>
                        <td>
                          <div className="user-cell">
                            <div className="avatar">{(a.email || '?')[0].toUpperCase()}</div>
                            <div>
                              <div className="user-email">{a.email}</div>
                              <div className="user-meta">
                                {[a.name, a.company].filter(Boolean).join(' · ')}
                                {(a.name || a.company) && ' · '}
                                <span className={'badge ' + planBadgeClass(a.plan_id)}>{a.plan_name || a.plan_id || 'free'}</span>{' '}
                                <span className={'badge ' + (a.status === 'active' ? 'badge-green' : 'badge-gray')}>{a.status}</span>
                                {a.account_type && a.account_type !== 'customer' && <> <span className={'badge badge-type ' + typeBadgeClass(a.account_type)}>{a.account_type}</span></>}
                              </div>
                              {(a.license_key || a.license_last8) && (
                                <div className="user-license">
                                  <code>…{a.license_key ? a.license_key.slice(-8) : a.license_last8}</code>
                                  {/* Copying the whole key needs Individuals write; read-only staff only get the last 8. */}
                                  {a.license_key && <CopyKeyButton value={a.license_key} />}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="credits-pill">
                            <div className="credits-bar"><div className="credits-fill" style={{ width: `${pct}%`, background: barColor }} /></div>
                            <span className="mono small">{a.credits_used_total} / {total} · {a.sites_count}/{a.max_sites || '∞'} sites</span>
                          </div>
                        </td>
                        <td className="muted">{new Date(a.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="row-actions">
                            <button className="btn-icon" title="Usage chart" onClick={() => toggle(a.id, 'usage')}><Icon name="trending" size={15} /></button>
                            <button className="btn-icon" title={`Sites (${accountSites.length})`} onClick={() => toggle(a.id, 'sites')}><Icon name="globe" size={15} /></button>
                            {canWrite && <button className="btn-icon" title="Conversations" onClick={() => toggle(a.id, 'conversations')}><Icon name="chat" size={15} /></button>}
                            {canWrite && <button className="btn-icon" title="Send email" onClick={() => toggle(a.id, 'email')}><Icon name="mail" size={15} /></button>}
                            <button className="btn-icon" title="View details" onClick={() => toggle(a.id, 'view')}><Icon name="eye" size={15} /></button>
                            {canWrite && <button className="btn-icon" title="Edit" onClick={() => toggle(a.id, 'edit')}><Icon name="pencil" size={15} /></button>}
                            {canWrite && <button className="btn-icon danger" title="Delete" onClick={() => remove(a)}><Icon name="trash" size={15} /></button>}
                          </div>
                        </td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (() => {
        const acct = accounts.find((a) => a.id === open.id);
        return acct ? (
          <AccountModal account={acct} mode={open.mode} plans={plans} sites={sites}
            onClose={() => setOpen(null)}
            onNote={(m) => setNote(m)}
            onSaved={(m) => { setNote(m); void load(); }} />
        ) : null;
      })()}
    </>
  );
}

/** Every account action (details, usage, sites, conversations, email, edit) now
 *  opens in a modal — mirrors the legacy popups. */
function AccountModal({ account, mode, plans, sites, onClose, onNote, onSaved }: {
  account: Account; mode: Mode; plans: Plan[]; sites: Site[];
  onClose: () => void; onNote: (m: string) => void; onSaved: (m: string) => void;
}) {
  useModalEscape(onClose);

  const accountSites = sites.filter((s) => s.account_email === account.email);
  const wide = mode === 'view' || mode === 'usage' || mode === 'sites' || mode === 'conversations';

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className={'modal account-modal' + (wide ? ' modal-wide' : '')} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="account-modal-title">
            {account.name
              ? <><h2 className="card-title" style={{ margin: 0 }}>{account.name}</h2><span className="muted small">{account.email}</span></>
              : <h2 className="card-title" style={{ margin: 0 }}>{account.email}</h2>}
          </div>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          {mode === 'view' && <ViewPanel account={account} />}
          {mode === 'usage' && <UsagePanel accountId={account.id} />}
          {mode === 'sites' && <SitesPanel sites={accountSites} />}
          {mode === 'conversations' && <ConversationsPanel accountId={account.id} email={account.email} />}
          {mode === 'email' && <EmailPanel email={account.email} onSent={(m) => { onNote(m); onClose(); }} />}
          {mode === 'edit' && <EditPanel account={account} plans={plans} onSaved={(m) => { onSaved(m); onClose(); }} />}
        </div>
      </div>
    </div>, document.body);
}

function AddAccountForm({ onCreated, onClose }: { onCreated: (msg: string) => void; onClose: () => void }) {
  const [f, setF] = useState({ email: '', name: '', company: '', accountType: 'customer', planId: 'standard' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const comped = f.accountType !== 'customer';

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const d = await backendApi.post<{ account?: { licenseKey?: string; license_key?: string } }>('/license/admin/accounts', {
        email: f.email.trim(), name: f.name.trim() || undefined, company: f.company.trim() || undefined,
        planId: f.planId, accountType: f.accountType,
      });
      const key = d.account?.licenseKey || d.account?.license_key;
      onCreated(`Account ${f.email.trim()} created.${key ? ` License: ${key}` : ''}`);
    } catch (err: any) { setError(err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="inline-form" onSubmit={submit}>
      <h4 className="detail-h" style={{ marginTop: 0 }}>Add account</h4>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <label className="field"><span>Email *</span>
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
        <label className="field"><span>Name</span>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="field"><span>Company</span>
          <input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></label>
      </div>
      <div className="form-row">
        <label className="field"><span>Account type</span>
          <select value={f.accountType} onChange={(e) => setF({ ...f, accountType: e.target.value })}>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="field"><span>Plan</span>
          <select value={f.planId} onChange={(e) => setF({ ...f, planId: e.target.value })}>
            {CREATE_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></label>
      </div>
      {comped && <p className="muted small" style={{ margin: '0 0 12px' }}>Comped account — created with plan credits, no Stripe / no subscription.</p>}
      <div className="row-actions">
        <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

/** Plan pill colour, as legacy: paid tiers purple, free/internal muted. */
function planBadgeClass(plan?: string | null) {
  return !plan || plan === 'free' || plan === 'internal' ? 'badge-gray' : 'badge-purple';
}

/** Account-type tag colour, as legacy: staff purple, pilot amber, everything
 *  else (partner_managed, grandfathered) teal — bold, so it reads at a glance. */
function typeBadgeClass(type: string) {
  return type === 'staff' ? 'badge-purple' : type === 'pilot' ? 'badge-amber' : 'badge-teal';
}

/** Copy the licence key and SAY so: the icon flips to a green tick for 1.5s,
 *  exactly like the legacy dashboard. Before this the click copied silently,
 *  which reads as a dead button. */
function CopyKeyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy(e: { stopPropagation: () => void }) {
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        // Non-secure origin fallback (clipboard API is unavailable over plain http).
        const ta = document.createElement('textarea');
        ta.value = value; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* leave the icon as-is; nothing to report */ }
  }
  return (
    <button className={'btn-icon' + (copied ? ' copied' : '')} title={copied ? 'Copied!' : 'Copy license key'} onClick={copy}>
      <Icon name={copied ? 'check' : 'copy'} size={13} />
    </button>
  );
}

function BroadcastForm({ count, filters, onSent, onClose }:
  { count: number; filters: Record<string, string>; onSent: (msg: string) => void; onClose: () => void }) {
  const [f, setF] = useState({ subject: '', body: '' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const filterParts = Object.values(filters);
  const filterDesc = filterParts.length ? ` (${filterParts.join(', ')})` : ' (all)';

  async function submit(e: FormEvent) {
    e.preventDefault(); setError('');
    if (!confirm(`Send this email to ${count} recipient${count === 1 ? '' : 's'}?`)) return;
    setBusy(true);
    try {
      const d = await backendApi.post<{ sent?: number; failed?: number }>('/license/admin/email/broadcast', {
        subject: f.subject.trim(), body: f.body,
        filters: Object.keys(filters).length ? filters : undefined,
      });
      onSent(`Broadcast sent: ${d.sent ?? 0} delivered, ${d.failed ?? 0} failed.`);
    } catch (err: any) { setError(err.body?.detail || err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit}>
      <p className="muted small" style={{ margin: '0 0 12px' }}>{count} recipient{count === 1 ? '' : 's'}{filterDesc} — matches the current filters.</p>
      {error && <div className="alert alert-error">{error}</div>}
      <label className="field"><span>Subject</span>
        <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></label>
      <label className="field"><span>Message</span>
        <textarea className="textarea" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
      <div className="row-actions">
        <button className="btn btn-primary btn-sm" disabled={busy || !count}>{busy ? 'Sending…' : `Send to ${count}`}</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

function ViewPanel({ account }: { account: Account }) {
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    backendApi.get<AccountDetail>(`/license/admin/accounts/${account.id}`)
      .then(setDetail).catch(() => setFailed(true));
  }, [account.id]);

  const acc = detail?.account || {};
  const stripe = acc.stripe_customer_id;
  const dSites = detail?.sites || [];
  const history = detail?.creditHistory || [];
  const ob = detail?.onboarding;
  const obData = ob?.onboarding_data || {};
  const clarity = `https://clarity.microsoft.com/projects/view/voobqrtmxs/recordings?filterBy=CustomUserId&CustomUserId=${encodeURIComponent(account.id)}`;
  const txClass = (t: string) => t === 'usage' ? 'badge-gray' : (t === 'addon' || t === 'purchase') ? 'badge-green' : 'badge-purple';
  const created = new Date(account.created_at).toLocaleDateString();

  const OB: Array<[string, string]> = [
    ['setup-workspace', 'Workspace'], ['find-us', 'How they found us'], ['head-count', 'Team size'],
    ['role', 'Role'], ['what-service', 'Services offered'], ['customer-profile', 'Ideal customers (ICP)'],
    ['hosting', 'Hosting'], ['toolkit', 'Toolkit'],
  ];
  const obRows = OB.filter(([k]) => obData[k] != null && String(obData[k]).trim() !== '').map(([k, label]) => {
    let v = String(obData[k]);
    if (k === 'setup-workspace') v = v === 'team' ? 'Team' : v === 'me' ? 'Just me' : v;
    return { label, v };
  });

  return (
    <>
      <div className="ad-header">
        <div>
          <h3 className="ad-email">{account.email}</h3>
          <span className="muted small">{[account.name, `Joined ${created}`].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="ad-badges">
          <span className="badge badge-purple">{account.plan_name || account.plan_id || 'free'}</span>
          <span className={'badge ' + (account.status === 'active' ? 'badge-green' : 'badge-gray')}>{account.status}</span>
        </div>
      </div>

      <div className="ad-stats">
        <div className="ad-stat"><span className="ad-stat-val">{(account.credits_balance ?? 0).toLocaleString()}</span><span className="ad-stat-lbl">Credits Balance</span></div>
        <div className="ad-stat"><span className="ad-stat-val">{account.sites_count ?? dSites.length}</span><span className="ad-stat-lbl">Sites Connected</span></div>
        <div className="ad-stat"><span className="ad-stat-val">{detail ? (stripe ? 'Yes' : 'No') : '…'}</span><span className="ad-stat-lbl">Stripe Customer</span></div>
        <div className="ad-stat"><span className="ad-stat-val">{ob?.onboarding_completed ? 'Completed' : 'In progress'}</span><span className="ad-stat-lbl">Onboarding</span></div>
      </div>

      <div style={{ margin: '14px 0' }}>
        <a className="btn btn-sm btn-ghost" href={clarity} target="_blank" rel="noopener noreferrer">
          <Icon name="eye" size={14} /> View Clarity Sessions
        </a>
      </div>

      <div className="ad-section-title">Details</div>
      <dl className="detail-grid">
        <dt>Company</dt><dd>{account.company || '—'}</dd>
        <dt>Type</dt><dd>{account.account_type || 'customer'}</dd>
        <dt>Credits used</dt><dd>{(account.credits_used_total ?? 0).toLocaleString()}</dd>
        <dt>Sites</dt><dd>{account.sites_count} / {account.max_sites || '∞'}</dd>
        <dt>Widgets</dt><dd>{account.widgets_count}</dd>
        <dt>License</dt><dd className="mono">{account.license_key
          || (account.license_last8 ? <>…{account.license_last8} <span className="muted small">(full key needs Individuals write)</span></> : '—')}</dd>
        <dt>Last login</dt><dd>{account.last_login_at ? new Date(account.last_login_at).toLocaleString() : 'never'}</dd>
      </dl>

      <div className="ad-section-title">Onboarding Answers</div>
      {failed ? <p className="muted small">Failed to load.</p>
        : !detail ? <p className="muted small">Loading…</p>
          : obRows.length === 0 ? <p className="muted small">No onboarding answers recorded.</p> : (
            <table className="table"><thead><tr><th>Question</th><th>Answer</th></tr></thead>
              <tbody>{obRows.map((r) => (<tr key={r.label}><td>{r.label}</td><td>{r.v}</td></tr>))}</tbody>
            </table>
          )}

      {dSites.length > 0 && (
        <>
          <div className="ad-section-title">Connected Sites</div>
          <table className="table">
            <thead><tr><th>Domain</th><th>Plugin Ver.</th><th>Last Seen</th></tr></thead>
            <tbody>{dSites.map((s, i) => {
              const u = s.site_url || s.domain || '';
              return (
                <tr key={i}>
                  <td className="mono small">{u
                    ? <a className="link" href={/^https?:\/\//.test(u) ? u : `https://${u}`} target="_blank" rel="noreferrer">{siteDomain(u)}</a>
                    : '—'}</td>
                  <td className="mono">{s.plugin_version || '—'}</td>
                  <td className="muted">{s.last_seen_at ? new Date(s.last_seen_at).toLocaleDateString() : 'Never'}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </>
      )}

      <div className="ad-section-title">Recent Credit Activity</div>
      {failed ? <p className="muted small">Failed to load account details.</p>
        : !detail ? <p className="muted small">Loading…</p>
          : history.length === 0 ? <p className="muted small">No credit activity yet.</p> : (
            <table className="table">
              <thead><tr><th>Type</th><th>Credits</th><th>Balance After</th><th>Description</th><th>Date</th></tr></thead>
              <tbody>{history.map((tx) => (
                <tr key={tx.id}>
                  <td><span className={'badge ' + txClass(tx.transaction_type)}>{tx.transaction_type}</span></td>
                  <td className="mono">{tx.credits_amount > 0 ? '+' : ''}{tx.credits_amount.toLocaleString()}</td>
                  <td className="mono">{tx.balance_after.toLocaleString()}</td>
                  <td>{tx.description || '—'}</td>
                  <td className="muted">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
    </>
  );
}

export function UsagePanel({ accountId }: { accountId: string }) {
  const [days, setDays] = useState<'7' | '30' | 'all'>('all');
  const [data, setData] = useState<UsageResp | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    backendApi.get<UsageResp>(`/license/admin/accounts/${accountId}/usage?days=${days}`)
      .then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [accountId, days]);

  const s = data?.summary;
  const usage = data?.usage || [];
  const txns = data?.transactions || [];
  const activeDays = usage.filter((u) => u.credits > 0 || u.messages > 0);
  const hasData = usage.some((u) => u.credits > 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h4 className="detail-h" style={{ marginTop: 0 }}>Usage</h4>
        <div className="seg">
          {(['7', '30', 'all'] as const).map((d) => (
            <button key={d} className={'seg-btn' + (days === d ? ' on' : '')} onClick={() => setDays(d)}>{d === 'all' ? 'All' : d + 'd'}</button>
          ))}
        </div>
      </div>
      {loading ? <p className="muted small">Loading…</p> : (
        <>
          {s && (
            <div className="uc-summary">
              <UcStat label="Given" value={s.totalAdmin || 0} color="#A78BFA" />
              <UcStat label="Purchased" value={s.totalPurchased || 0} color="var(--color-status-success-accent)" />
              <UcStat label="Used" value={s.totalUsed || 0} color="#EF4444" />
              <UcStat label="Messages" value={s.totalMessages || 0} />
            </div>
          )}
          {hasData ? <UsageLineChart usage={usage} /> : <p className="muted small">No usage data for this period.</p>}

          {txns.length > 0 && (
            <>
              <div className="uc-section-title">Credit Additions &amp; Adjustments</div>
              <div className="table-wrap">
                <table className="table uc-detail-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Credits</th><th>Balance</th><th>Description</th></tr></thead>
                  <tbody>{txns.map((t, i) => {
                    const isAdmin = (t.description || '').toLowerCase().includes('admin');
                    const label = isAdmin ? 'given' : t.type;
                    const cls = isAdmin || t.type === 'reset' ? 'badge-purple'
                      : t.type === 'refund' ? 'badge-gray' : 'badge-green';
                    return (
                      <tr key={i}>
                        <td className="muted">{new Date(t.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td><span className={'badge ' + cls}>{label}</span></td>
                        <td className="mono" style={{ color: t.amount > 0 ? 'var(--color-status-success-accent)' : '#EF4444', fontWeight: 600 }}>
                          {t.amount > 0 ? '+' : ''}{t.amount.toLocaleString()}</td>
                        <td className="mono">{t.balanceAfter.toLocaleString()}</td>
                        <td className="muted small">{t.description || '—'}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            </>
          )}

          {activeDays.length > 0 && (
            <>
              <div className="uc-section-title">Daily Usage</div>
              <div className="table-wrap">
                <table className="table uc-detail-table">
                  <thead><tr><th>Date</th><th>Messages</th><th>Credits</th><th>API Cost</th><th>$/Credit</th></tr></thead>
                  <tbody>{activeDays.map((d) => (
                    <tr key={d.date}>
                      <td>{new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="mono">{d.messages}</td>
                      <td className="mono">{d.credits.toLocaleString()}</td>
                      <td className="mono">${(d.cost || 0).toFixed(2)}</td>
                      <td className="muted mono">{d.credits > 0 ? '$' + ((d.cost || 0) / d.credits).toFixed(2) : '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

/** Boxed summary stat (Given / Purchased / Used / Messages). */
function UcStat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="uc-stat">
      <span className="uc-stat-val" style={color ? { color } : undefined}>{value.toLocaleString()}</span>
      <span className="uc-stat-lbl">{label}</span>
    </div>
  );
}

/** Smoothed credits-per-day line chart with a gradient fill and hover tooltip
 *  (mirrors legacy renderUsageChart's per-account chart). */
function UsageLineChart({ usage }: { usage: { date: string; credits: number; messages: number; cost?: number }[] }) {
  const [hover, setHover] = useState<{ i: number; cx: number; cy: number } | null>(null);
  const W = 820, H = 170, padL = 10, padR = 10, padT = 18, padB = 34;
  const chartW = W - padL - padR, chartH = H - padT - padB, baseline = padT + chartH;
  const n = usage.length;
  const max = Math.max(1, ...usage.map((u) => u.credits));
  const xAt = (i: number) => n === 1 ? padL + chartW / 2 : padL + (chartW * i) / (n - 1);
  const pts = usage.map((u, i) => [xAt(i), padT + (1 - u.credits / max) * chartH] as [number, number]);
  const ticks = tickIdxs(n);
  const hi = hover && hover.i < n ? hover.i : null;

  function move(e: MouseEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const mx = ((e.clientX - r.left) / r.width) * W;
    let idx = 0, min = Infinity;
    for (let i = 0; i < n; i++) { const d = Math.abs(xAt(i) - mx); if (d < min) { min = d; idx = i; } }
    setHover({ i: idx, cx: e.clientX, cy: e.clientY });
  }

  return (
    <>
      <svg className="chart-svg chart-interactive" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Credits used per day"
        onMouseMove={move} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="uc-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={`${smooth(pts)} L${xAt(n - 1)},${baseline} L${xAt(0)},${baseline} Z`} fill="url(#uc-fill)" />
        <path d={smooth(pts)} fill="none" stroke="#3B82F6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {ticks.map((i) => (
          <text key={i} x={xAt(i)} y={H - 8} fontSize={11} textAnchor="middle" fill="var(--color-text-tertiary)">{fmtDate(usage[i].date)}</text>
        ))}
        {hi != null && (
          <g pointerEvents="none">
            <line x1={xAt(hi)} x2={xAt(hi)} y1={padT} y2={baseline} strokeWidth={1}
              stroke="var(--color-text-tertiary)" strokeDasharray="4,3" opacity={0.7} />
            <circle cx={pts[hi][0]} cy={pts[hi][1]} r={4} fill="var(--color-surface-default)" stroke="#3B82F6" strokeWidth={2} />
          </g>
        )}
      </svg>
      {hover && hi != null && createPortal(
        <div className="chart-tooltip" style={{
          left: Math.min(hover.cx + 16, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 240),
          top: Math.max(hover.cy - 20, 8),
        }}>
          <div className="tt-date">{fmtDateFull(usage[hi].date)}</div>
          <div className="tt-total">{usage[hi].credits.toLocaleString()} credits</div>
          <div className="tt-row"><span className="tt-name">Messages</span><span className="tt-val">{usage[hi].messages}</span></div>
          {usage[hi].cost ? <div className="tt-row"><span className="tt-name">API cost</span><span className="tt-val">${(usage[hi].cost || 0).toFixed(2)}</span></div> : null}
        </div>, document.body)}
    </>
  );
}

/** Strip protocol + trailing slash for a compact clickable domain label. */
export function siteDomain(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function SitesPanel({ sites }: { sites: Site[] }) {
  return (
    <>
      <h4 className="detail-h" style={{ marginTop: 0 }}>Connected Sites ({sites.length})</h4>
      {sites.length === 0 ? <p className="muted small">No registered sites.</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Site</th><th>Plugin</th><th>WP</th><th>Last Seen</th><th>Status</th></tr></thead>
            <tbody>{sites.map((s) => {
              const url = s.site_url || '';
              return (
                <tr key={s.id}>
                  <td className="mono small">{url
                    ? <a className="link" href={/^https?:\/\//.test(url) ? url : `https://${url}`} target="_blank" rel="noreferrer">{siteDomain(url)}</a>
                    : s.id}</td>
                  <td className="mono">{s.plugin_version || '—'}</td>
                  <td className="mono">{s.wp_version || '—'}</td>
                  <td className="muted">{s.last_seen_at ? new Date(s.last_seen_at).toLocaleDateString() : '—'}</td>
                  <td><span className={'badge ' + (s.is_active !== false ? 'badge-green' : 'badge-gray')}>{s.is_active !== false ? 'active' : 'inactive'}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Compact "time ago" (2h, 3d, 5w…) matching the legacy conversation cards. */
function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 60) return 'now';
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24); if (days < 7) return `${days}d`;
  const wks = Math.floor(days / 7); if (wks < 5) return `${wks}w`;
  const mos = Math.floor(days / 30); if (mos < 12) return `${mos}mo`;
  return `${Math.floor(days / 365)}y`;
}

function ConversationsPanel({ accountId, email }: { accountId: string; email: string }) {
  const [convos, setConvos] = useState<Conversation[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    backendApi.get<{ conversations: Conversation[] }>(`/license/admin/accounts/${accountId}/conversations`)
      .then((d) => setConvos(d.conversations || [])).catch(() => setConvos([]));
  }, [accountId]);

  return (
    <>
      <h4 className="detail-h" style={{ marginTop: 0 }}>Conversations{convos ? ` (${convos.length})` : ''} — <strong>{email}</strong></h4>
      {!convos ? <p className="muted small">Loading…</p> : convos.length === 0 ? <p className="muted small">No conversations yet.</p> : (
        <div className="conv-grid">
          {convos.map((c) => (
            <button key={c.id} type="button" className="conv-card" onClick={() => setOpenId(c.id)}>
              <div className="conv-card-head">
                <span className="conv-card-title">{c.title || 'Untitled'}</span>
                <span className="conv-card-time">{timeAgo(c.last_message_at || c.created_at)}</span>
              </div>
              <div className="conv-card-preview">{c.first_message ? c.first_message.slice(0, 90) : 'No messages'}</div>
              <div className="conv-card-foot">
                <span className="conv-card-msgs">{c.message_count ?? 0} msgs</span>
                {c.site_url && <span className="conv-card-site">{siteDomain(c.site_url)}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {openId && <ConversationModal conversationId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

export function ConversationModal({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const [data, setData] = useState<{ conversation?: any; messages?: Message[] } | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    backendApi.get<{ conversation?: any; messages?: Message[] }>(`/license/admin/conversations/${conversationId}/messages`)
      .then(setData).catch(() => setFailed(true));
  }, [conversationId]);
  useModalEscape(onClose);

  const conv = data?.conversation;
  const messages = data?.messages || [];
  const totalCost = messages.reduce((s, m) => s + (m.cost_usd || 0), 0);
  const site = conv?.site_url ? siteDomain(conv.site_url) : '';
  const meta = [
    conv?.account_email, site,
    conv?.created_at && new Date(conv.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    `${messages.length} messages`,
    totalCost > 0 ? `$${totalCost.toFixed(4)}` : '',
  ].filter(Boolean).join(' · ');

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal conv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{failed ? 'Conversation' : (conv?.title || 'Conversation')}</h2>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        {!failed && data && <p className="muted small conv-modal-meta">{meta}</p>}
        <div className="conv-modal-body">
          {failed ? <p className="muted small">Failed to load conversation.</p>
            : !data ? <p className="muted small">Loading messages…</p>
              : messages.length === 0 ? <p className="muted small">No messages in this conversation.</p>
                : messages.map((m) => {
                  const tokens = (m.input_tokens || 0) + (m.output_tokens || 0);
                  const role = m.role === 'user' ? 'User' : m.role === 'system' ? 'System' : 'Assistant';
                  return (
                    <div key={m.id} className={'conv-msg conv-msg-' + m.role}>
                      <div className="conv-msg-head">
                        <span className="conv-msg-role">{role}</span>
                        {m.model && <span className="mono conv-msg-model">{m.model}</span>}
                        {tokens > 0 && <span className="conv-msg-tokens">{tokens.toLocaleString()} tok</span>}
                        {m.cost_usd ? <span className="conv-msg-tokens">${m.cost_usd.toFixed(4)}</span> : null}
                        {m.created_at && <span className="conv-msg-time">{new Date(m.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                      </div>
                      <div className="conv-msg-content">{m.content || ''}</div>
                    </div>
                  );
                })}
        </div>
      </div>
    </div>, document.body);
}

export function EmailPanel({ email, onSent }: { email: string; onSent: (msg: string) => void }) {
  const [f, setF] = useState({ subject: '', body: '' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      await backendApi.post('/license/admin/email/send', {
        to: email, subject: f.subject.trim(), body: f.body,
      });
      onSent(`Email sent to ${email}.`);
    } catch (err: any) { setError(err.body?.detail || err.body?.error || err.message); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit}>
      <h4 className="detail-h" style={{ marginTop: 0 }}>Compose email</h4>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <label className="field"><span>From</span>
          <input value="WPOS <noreply@wpos.ai>" readOnly disabled /></label>
        <label className="field"><span>To</span>
          <input value={email} readOnly disabled /></label>
      </div>
      <label className="field"><span>Subject</span>
        <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></label>
      <label className="field"><span>Message</span>
        <textarea className="textarea" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Sending…' : 'Send email'}</button>
    </form>
  );
}

function EditPanel({ account, plans, onSaved }:
  { account: Account; plans: Plan[]; onSaved: (msg: string) => void }) {
  const [f, setF] = useState({
    planId: account.plan_id || '', status: account.status,
    accountType: account.account_type || 'customer', credits: '',
  });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(''); setBusy(true);
    const body: any = {};
    if (f.planId && f.planId !== account.plan_id) body.planId = f.planId;
    if (f.status !== account.status) body.status = f.status;
    if (f.accountType !== (account.account_type || 'customer')) body.accountType = f.accountType;
    if (f.credits.trim()) body.credits = Number(f.credits);
    if (Object.keys(body).length === 0) { setError('Nothing changed.'); setBusy(false); return; }
    try {
      await backendApi.put(`/license/admin/accounts/${account.id}`, body);
      onSaved(`${account.email} updated.`);
    } catch (err: any) { setError(err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <label className="field"><span>Plan</span>
          <select value={f.planId} onChange={(e) => setF({ ...f, planId: e.target.value })}>
            <option value="">— keep {account.plan_name || account.plan_id || 'current'} —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
          </select></label>
        <label className="field"><span>Status</span>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {['active', 'suspended', 'cancelled'].map((s) => <option key={s}>{s}</option>)}
          </select></label>
        <label className="field"><span>Account type</span>
          <select value={f.accountType} onChange={(e) => setF({ ...f, accountType: e.target.value })}>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="field"><span>Add credits (optional)</span>
          <input type="number" placeholder="e.g. 500" value={f.credits}
            onChange={(e) => setF({ ...f, credits: e.target.value })} /></label>
      </div>
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
    </form>
  );
}
